/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {BannerEl} from 'common/components/banner';
import {BannerType} from 'common/components/banner/types';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {ChartTooltipEl} from 'common/components/chart-tooltip';
import {DropMenuEl} from 'common/components/drop-menu';
import {Directions} from 'common/components/drop-menu/types';
import {DropMenuPaneEl} from 'common/components/drop-menu-pane';
import {ItemsMenuEl} from 'common/components/items-menu';
import {Section as ItemsMenuSection} from 'common/components/items-menu/types';
import {PopoverCardEl} from 'common/components/popover-card';
import {SidenavEl} from 'common/components/sidenav';
import {ToastEl} from 'common/components/toast';
import {ToastPortalEl} from 'common/components/toast-portal';
import {TooltipEl} from 'common/components/tooltip';
import {DisplaySize, Placement, PositioningStrategy} from 'common/components/tooltip/types';
import COLORS from 'common/stylesheets/mixins/colors.json';
import {ActionsPopoverEl} from 'common/widgets/actions-popover';
import {UpsellPopupEl} from 'common/widgets/upsell-popup';
import {UserIconEl} from 'common/widgets/user-icon';
import {IconSizeType} from 'common/constants';

import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import tooltipImg from '../../assets/tooltip-image.svg';
import avatarSm from '../../assets/avatars/small.png';
import toastSemanticsRight from '../../assets/toast-semantics-right.png';
import toastSemanticsWrong from '../../assets/toast-semantics-wrong.png';
import toastLocation from '../../assets/toast-location.png';
import toastStacking from '../../assets/toast-stacking.png';
import toastEmbedding from '../../assets/toast-embedding.png';
import toastInjecting from '../../assets/toast-injecting.png';
import {AutoDoc, Section, SubSection} from './utils';

export function MenusSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Menus, Toasts & Tooltips'>
      <SubSection title='mp-drop-menu' autoDoc={true}>
        <p sel='.desc'>
          Use drop menu to show a contextual sub view when a button is clicked. A number of other components use this as
          a base component for composition
        </p>

        <CodeTipEl sel='.example'>
          <ButtonEl attrs={{theme: ButtonTheme.Primary}} on={{click: designSystem.helpers.toggleMenu}}>
            Drop Menu
          </ButtonEl>
          <div sel='.drop-menu-container'>
            <DropMenuEl
              attrs={{open: designSystem.state.open.menu}}
              on={{change: (ev) => designSystem.helpers.handleMenuChange(`menu`, ev.detail.state)}}
            >
              <div sel='.emoji'>🦊</div>
            </DropMenuEl>
          </div>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Primary, icon: `caret-up`}}
            on={{click: designSystem.helpers.toggleMenuUp}}
          >
            Up Menu
          </ButtonEl>
          <div sel='.drop-menu-container' style={{bottom: `175px`}}>
            <DropMenuEl
              attrs={{
                open: designSystem.state.open.menuUp,
                direction: Directions.Up,
              }}
              on={{change: (ev) => designSystem.helpers.handleMenuChange(`menuUp`, ev.detail.state)}}
            >
              <div sel='.emoji'>🐣</div>
            </DropMenuEl>
          </div>
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Primary, icon: `caret-down`}}
            on={{click: designSystem.helpers.toggleCaretMenu}}
          >
            Caret Position
          </ButtonEl>
          <div sel='.drop-menu-container'>
            <DropMenuEl
              attrs={{
                open: designSystem.state.open.caretMenu,
                'caret-pos': JSON.stringify({left: `18px`}),
              }}
              on={{change: (ev) => designSystem.helpers.handleModalChange(`caretMenu`, ev.detail.state)}}
            >
              <div sel='.emoji'>🦄</div>
            </DropMenuEl>
          </div>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-drop-menu-pane' autoDoc={true}>
        <p sel='.desc'>Use drop menu pane to render a title bar and body inside a drop menu.</p>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Primary, icon: `caret-down`}}
            on={{click: designSystem.helpers.toggleDropPaneMenu}}
          >
            Drop Menu Pane
          </ButtonEl>
          <div sel='.drop-menu-container'>
            <DropMenuEl
              attrs={{open: designSystem.state.open.dropPaneMenu}}
              on={{change: (ev) => designSystem.helpers.handleModalChange(`dropPaneMenu`, ev.detail.state)}}
            >
              <DropMenuPaneEl attrs={{header: `Drop Menu Pane`, 'show-back-button': true}}>
                <div sel='.emoji'>🐼</div>
              </DropMenuPaneEl>
            </DropMenuEl>
          </div>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-tooltip' autoDoc={false}>
        <div sel='.examples.basic'>
          <div sel='.left'>
            <CodeTipEl sel='.block.example.simple'>
              <div sel='.example-tooltip'>
                <TooltipEl attrs={{'always-show': true, 'position-strategy': PositioningStrategy.Absolute}}>
                  Simple Tooltip
                </TooltipEl>
              </div>
            </CodeTipEl>

            <CodeTipEl sel='.block.example.medium'>
              <div sel='.example-tooltip'>
                <TooltipEl
                  attrs={{
                    'always-show': true,
                    text: `Rich Tooltip (Medium) \nUp to three lines of text max. \n[Can contain a link](https://mixpanel.com)`,
                    placement: Placement.Right,
                    'image-src': tooltipImg,
                    size: DisplaySize.Medium,
                    'position-strategy': PositioningStrategy.Absolute,
                  }}
                />
              </div>
            </CodeTipEl>
          </div>

          <div sel='.right'>
            <CodeTipEl sel='.block.example.small'>
              <div sel='.example-tooltip'>
                <TooltipEl
                  attrs={{
                    'always-show': true,
                    text: `Rich Tooltip (small)`,
                    placement: Placement.Right,
                    size: DisplaySize.Small,
                    'position-strategy': PositioningStrategy.Absolute,
                  }}
                >
                  <UserIconEl
                    attrs={{
                      name: `Brandon S.`,
                      'img-src': avatarSm,
                      'include-label': true,
                      'disable-tooltip': true,
                      size: IconSizeType.Small,
                    }}
                  />
                </TooltipEl>
              </div>
            </CodeTipEl>

            <CodeTipEl sel='.block.example.large'>
              <div sel='.example-tooltip'>
                <TooltipEl
                  attrs={{
                    'always-show': true,
                    text: `Rich Tooltip (Large) \nUp to three lines of text max. \n[Can contain a link](https://mixpanel.com)`,
                    placement: Placement.Right,
                    'image-src': tooltipImg,
                    size: DisplaySize.Large,
                    'position-strategy': PositioningStrategy.Absolute,
                  }}
                />
              </div>
            </CodeTipEl>
          </div>
        </div>

        <div sel='.mp-panel-section'>
          <h3>Overview</h3>

          <p sel='.desc'>
            Tooltips should:
            <ul>
              <li>• Display helpful scannable information on hover</li>
              <li>• Describe differences between similar elements</li>
              <li>• Be paired with icon only buttons, or buttons with keyboard shortcuts</li>
            </ul>
          </p>

          <p sel='.desc'>
            Tooltips should not:
            <ul>
              <li>• Not be used to communicate critical information, errors, or other interaction feedback</li>
            </ul>
          </p>

          <p sel='.desc'>
            Interactions:
            <ul>
              <li>• Tooltips should appear on hover/focus</li>
              <li>• Link text should change opacity on hover from 60% to 90%</li>
              <li>
                • Have a <span sel='.strong'>delay-in</span> & <span sel='.strong'>delay-out</span>
              </li>
              <li>
                • The delay-out should be cancelled if a user quickly mouses over to a nearby element with tooltip (ex:
                hovering over a series of icons in the top navigation)
              </li>
            </ul>
          </p>

          <p sel='.desc'>
            Types: <br /> <span sel='.strong'>Simple Tooltips</span> & <span sel='.strong'>Rich Tooltips</span>
            {` `}
            (Small/Medium/Large)
          </p>
        </div>

        <div sel='.mp-panel-section'>
          <h3>Simple Tooltips</h3>
          <p sel='.desc'>
            Simple tooltip content:
            <ul>
              <li>
                • Short descriptive text (should <span sel='.strong'>not</span> restate visible UI text)
              </li>
              <li>• Written in title case</li>
            </ul>
          </p>

          <AutoDoc componentName='mp-tooltip'>
            <div sel='.examples'>
              <div sel='.left'>
                <CodeTipEl sel='.example'>
                  <div sel='.example-button'>
                    <ButtonEl attrs={{icon: `refresh`, theme: ButtonTheme.Primary}} />
                    <TooltipEl attrs={{placement: Placement.Bottom}}>Refresh</TooltipEl>
                  </div>
                </CodeTipEl>

                <ul>
                  <li>👍 Succinct descriptive text</li>
                  <li>👍 Title Case</li>
                  <li>👍 Directly paired with an icon button</li>
                </ul>
              </div>

              <div sel='.right'>
                <CodeTipEl sel='.example'>
                  <div sel='.example-button'>
                    <ButtonEl attrs={{theme: ButtonTheme.Primary}}>Add to Dashboard</ButtonEl>
                    <TooltipEl attrs={{placement: Placement.Bottom}}>add to dashboard</TooltipEl>
                  </div>
                </CodeTipEl>

                <ul>
                  <li>❌ Restate text in the UI</li>
                  <li>❌ all lowercase</li>
                </ul>
              </div>
            </div>
          </AutoDoc>
        </div>

        <div sel='.mp-panel-section'>
          <h3>Rich Tooltips</h3>
          <p sel='.desc'>
            Rich tooltip content:
            <ul>
              <li>• Descriptive sentence structure text</li>
              <li>• Optional: image, video, avatar photo, link to docs</li>
            </ul>
          </p>

          <div sel='.examples.rich'>
            <div sel='.left'>
              <CodeTipEl sel='.example'>
                <ItemsMenuEl
                  attrs={{sections: designSystem.helpers.getRichTooltipItemsMenuSections(true) as ItemsMenuSection[]}}
                />
              </CodeTipEl>
              <ul>
                <li>👍 Descriptive text</li>
                <li>👍 Sentence case</li>
                <li>👍 Link at the bottom</li>
                <li>👍 Directly paired with an element</li>
              </ul>
            </div>

            <div sel='.right'>
              <CodeTipEl sel='.example'>
                <ItemsMenuEl
                  attrs={{sections: designSystem.helpers.getRichTooltipItemsMenuSections(false) as ItemsMenuSection[]}}
                />
              </CodeTipEl>
              <ul>
                <li>❌ Link is in the middle</li>
                <li>❌ Verbose and not helpful</li>
                <li>❌ Title Case</li>
              </ul>
            </div>
          </div>
        </div>
      </SubSection>

      <SubSection title='mp-chart-tooltip' autoDoc={true}>
        <p sel='.desc'>
          Use this tooltip style for when interacting with and providing more information on charts on hover.
        </p>

        <CodeTipEl sel='.example'>
          <ChartTooltipEl
            attrs={{sections: designSystem.state.chartTooltipSections, 'title-color': COLORS[`$gray3`]}}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-toast' autoDoc={false}>
        <p sel='.desc'>
          To indicate success after a user action that involves a network call, or an unobtrusive notification e.g. new
          data is available.
        </p>

        <h3>Types</h3>
        <p sel='.desc'>
          When evaluating which type of toast to use, presecribe to semantic guides first, and follow the type with the
          method of presentation.
        </p>

        <h4>Informational</h4>
        <ToastEl attrs={{type: `info`}}>An Informational Notification.</ToastEl>

        <h4>Warning</h4>
        <ToastEl attrs={{type: `warning`}}>A Warning Notification.</ToastEl>

        <h4>Success</h4>
        <ToastEl attrs={{type: `success`}}>A Successful Notification.</ToastEl>

        <h4>Desctructive</h4>
        <ToastEl attrs={{type: `error`}}>A Destructive Notification.</ToastEl>

        <div sel='.divider-line' />

        <h3>Related Components</h3>
        <h4>When do I use a Toast over Banners, and vice versa?</h4>
        <p sel='.desc'>
          The easiest answer is: if you're making a network call to find out if you should display something: Toast. If
          you're displaying something upon load of a page, modal, view or using form validation you should be using a
          Banner.
        </p>

        <h4>Banner</h4>
        <BannerEl
          attrs={{
            theme: `warn`,
            'header-text': `You haven't set up boards yet.`,
            'body-text': `You haven't set up boards yet.`,
            cta: JSON.stringify({
              text: `Dismiss`,
              theme: `secondary`,
              actionName: `dismiss`,
            }),
          }}
        />

        <br />

        <h4>Toast</h4>
        <ToastEl attrs={{type: `success`}}>A Successful Notification.</ToastEl>

        <div sel='.divider-line' />

        <h3>Examples</h3>
        <p sel='.desc'>
          Toasts should appear in the bottom left corner of a screen's interface. It should not be embedded within a
          container.
        </p>

        <h4>Semantically Appropriate</h4>
        <BannerEl
          attrs={{
            theme: `success`,
            type: BannerType.Embedded,
            'header-text': `Do`,
            'body-text': `Use the semantically correct type of toast with the message being shown.`,
          }}
        />
        <img sel='.example-image' attrs={{src: toastSemanticsRight}} />
        <br />
        <br />
        <BannerEl
          attrs={{
            theme: `error`,
            type: BannerType.Embedded,
            'header-text': `Do Not`,
            'body-text': `Do not Use the semantically inappropriate toast types - actively not matching the toast message.`,
          }}
        />
        <img sel='.example-image' attrs={{src: toastSemanticsWrong}} />
        <h5>What's wrong with the toasts above?</h5>
        <ul>
          <li>
            1. New Information available should be an informative toast as it's showing new information and not success.
          </li>
          <li>2. System error should be a warning toast as it's a system-side issue, and not a desctructive issue.</li>
          <li>
            3. Report deleted should be a desctructive toast as it's not a system-side issue, but a desctructive action
            being shown.
          </li>
          <li>4. Report saved should be a success toast as it's indicating success.</li>
        </ul>
        <br />
        <br />

        <h4>Location</h4>
        <BannerEl
          attrs={{
            theme: `success`,
            type: BannerType.Embedded,
            'header-text': `Do`,
            'body-text': `Place toast as a float object in the bottom left corner of your screen.`,
          }}
        />
        <img sel='.example-image' attrs={{src: toastLocation}} />
        <br />
        <br />

        <h4>Stacking</h4>
        <BannerEl
          attrs={{
            theme: `success`,
            type: BannerType.Embedded,
            'header-text': `Do`,
            'body-text': `Stack Toast notifications if necessary. New Toast notifications should appear above the notification it preceeds, while allowing standard animation playback of it’s animation in, as well as out. They should naturally progress in and out based on when they initially show up on screen.`,
          }}
        />
        <img sel='.example-image' attrs={{src: toastStacking}} />
        <br />
        <br />

        <h4>Embedding</h4>
        <BannerEl
          attrs={{
            theme: `error`,
            type: BannerType.Embedded,
            'header-text': `Do Not`,
            'body-text': `Do not embed Toast within another object on screen.`,
          }}
        />
        <img sel='.example-image' attrs={{src: toastEmbedding}} />
        <br />
        <br />

        <h4>Injecting</h4>
        <BannerEl
          attrs={{
            theme: `error`,
            type: BannerType.Embedded,
            'header-text': `Do Not`,
            'body-text': `Do not inject a Toast in the place of a Banner.`,
          }}
        />
        <img sel='.example-image' attrs={{src: toastInjecting}} />
        <br />

        <div sel='.divider-line' />

        <AutoDoc componentName='mp-toast'>
          <CodeTipEl sel='.example'>
            <ToastEl attrs={{type: `success`}}>A Successful Notification.</ToastEl>
          </CodeTipEl>
          <ButtonEl attrs={{theme: ButtonTheme.Secondary, icon: `plus`}} on={{click: designSystem.helpers.addToast}}>
            Toast
          </ButtonEl>
          {(designSystem.state.toasts || []).map((toast) => (
            <ToastEl
              attrs={{cta: toast.cta, hide: toast.closed, undoable: toast.undoable, type: toast.type}}
              on={{close: () => designSystem.helpers.closeToast(toast)}}
            >
              {toast.message}
            </ToastEl>
          ))}
        </AutoDoc>
      </SubSection>

      <SubSection title='mp-toast-portal' autoDoc={true}>
        <p sel='.desc'>
          This is the declarative component way to add a toast, directly inside of a template. Also supports messages
          with HTML.
        </p>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary, icon: `plus`}}
            on={{click: designSystem.helpers.handleStringToastPortalClick}}
          >
            String toast
          </ButtonEl>
          {designSystem.state.stringToastPortalId > 0 && (
            <ToastPortalEl
              props={{
                toast: {
                  id: `string-toast-${designSystem.state.stringToastPortalId}`,
                  isStatic: true,
                  hide: false,
                  message: `I am a normal string message # ${designSystem.state.stringToastPortalId}`,
                },
              }}
            />
          )}
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary, icon: `plus`}}
            on={{click: designSystem.helpers.handleHTMLToastPortalClick}}
          >
            Toast with link
          </ButtonEl>
          {designSystem.state.HTMLToastPortalId > 0 && (
            <ToastPortalEl
              props={{
                toast: {
                  id: `html-toast-${designSystem.state.HTMLToastPortalId}`,
                  isStatic: true,
                  hide: false,
                  message: (
                    <div sel='.text'>
                      I am a message with <a attrs={{href: `#`}}>a link</a> # {designSystem.state.HTMLToastPortalId}
                    </div>
                  ),
                },
              }}
            />
          )}
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-upsell-popup' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{click: () => designSystem.helpers.openModal(`mp-upsell-popup`)}}
          >
            Open up the upsell popup
          </ButtonEl>
          <UpsellPopupEl
            attrs={{open: designSystem.state.open[`mp-upsell-popup`]}}
            on={{
              change: (ev: CustomEvent) =>
                ev.detail.state === `closed` ? designSystem.helpers.closeModal(`mp-upsell-popup`) : null,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-popover-card' autoDoc={true}>
        <CodeTipEl sel='.example.height'>
          <PopoverCardEl
            attrs={{
              title: `Viewed report`,
              'button-options': designSystem.helpers.getPopoverButtonOptions(),
            }}
          >
            <div sel='.mp-popover-slotted-body'>
              <div sel='.emoji'>🥤</div>
            </div>
          </PopoverCardEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-actions-popover' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Primary}}
            on={{click: () => designSystem.helpers.openModal(`mp-actions-popover`)}}
          >
            Click me
          </ButtonEl>
          <ActionsPopoverEl
            attrs={{
              title: `Viewed report`,
              'button-options': designSystem.helpers.getPopoverButtonOptions(),
              'cohort-details': designSystem.helpers.getPopoverCohortDetails(),
              open: designSystem.state.open[`mp-actions-popover`],
            }}
            on={{
              close: () => designSystem.helpers.closeModal(`mp-actions-popover`),
            }}
          >
            <div sel='.mp-popover-slotted-body'>
              <div sel='.emoji'>🥳</div>
            </div>
          </ActionsPopoverEl>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-sidenav' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <SidenavEl
            attrs={{
              selected: `active`,
              header: {
                title: `Reports`,
                slot: `header`,
              },
            }}
            props={{
              sections: designSystem.helpers.getSidenavSections(),
            }}
            style={{
              height: `600px`,
              width: `${designSystem.state.sidenavWidth}px`,
            }}
            on={{
              resize: designSystem.helpers.onSidenavResize,
            }}
          >
            <div attrs={{slot: `header`}}>I am a slotted header!</div>
            <div attrs={{slot: `footer`}}>I am a slotted Footer!</div>
          </SidenavEl>
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
